import { createFeatureSelector, createSelector } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';

import * as Objects from '@core/objects';

import { VersionShareItem } from '@core/models/version-share.model';


// in snake style for help to find
export interface ProjectRVErrors {
    load_related: HttpErrorResponse | null,
    load_feedbacks: HttpErrorResponse | null,
}

export interface ProjectRVStarting {
    initialized: boolean;
    project_ready: boolean;
    related_ready: boolean;
    feedbacks_ready: boolean;
}

export interface ProjectRelatedVersions {
    project: Objects.Request;
    files: Array<Objects.File>;
    origin_version: Objects.Version;
    related_versions: Array<Objects.Version>;
    related_versions_groups: Array<Objects.RelatedVersions>;
    feedbackAccess: boolean | null;
    feedbacks: VersionShareItem[] | null;
    errors: ProjectRVErrors;
    starting: ProjectRVStarting;
}


export const initialState: ProjectRelatedVersions = {
    starting: {
        initialized: false,
        project_ready: false,
        related_ready: false,
        feedbacks_ready: false,
    },
    project: null,
    files: [],
    origin_version: null,
    related_versions: [],
    related_versions_groups: [],
    feedbackAccess: null,
    feedbacks: null,
    errors: {
        load_related: null,
        load_feedbacks: null
    }
};



const projectRelatedVersionsSelector = createFeatureSelector('project_related_versions');

export const projectRelatedVersionsFeedbacksAccessSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state?.feedbackAccess
);

export const projectSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state?.project
);

export const filesSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state.files
);

export const originVersionSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state.origin_version
);

export const relatedVersionsSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state.related_versions_groups
);

export const relatedVersionsFeedbacksSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state.feedbacks
);


export const projectVersionsFeedbacksSelector = (versions: Objects.Version[]) =>
    createSelector(
        projectRelatedVersionsSelector,
        (state: ProjectRelatedVersions) => {
            if (!state || !state.feedbacks) {
                return null; // or any default value based on your use case
            }

            const versionIds = versions.map((version) => version.id);

            return state.feedbacks.filter((vf: VersionShareItem) =>
                versionIds.includes(vf.version.id)
            );
        }
    );

export const errorsSelector  =
    createSelector(
        projectRelatedVersionsSelector,
        (state: ProjectRelatedVersions) =>  state?.errors
    );

// Generic selector by key (leave factory name as-is)
export const errorSelector = (key: keyof ProjectRVErrors) =>
    createSelector(
        projectRelatedVersionsSelector,
        (state: ProjectRelatedVersions) => (key in state?.errors) ? state.errors[key] : null
    );

export const startingSelector = createSelector(
    projectRelatedVersionsSelector,
    (state: ProjectRelatedVersions) => state?.starting
);

// Generic selector by key (keep factory name)
export const actionStartingSelector = (key: keyof ProjectRVStarting) =>
    createSelector(
        projectRelatedVersionsSelector,
        (state: ProjectRelatedVersions) => state?.starting ? state.starting[key] : false
    );
